import {
  AnalyticsPageName,
  BacktestingRunType,
  BaseBotData,
  BasicBotData,
  BotData,
  BotGraphType,
  BotStatus,
  BotType,
  CustomStrategyData,
  DisplayResultMessage,
  FormattedBotsGraphsData,
  FormattedChartDataPoint,
  GetBotGraphDataRequest,
  GetBotsGraphsDataRequest,
  GetPublicBotGraphDataRequest,
  LanguageNameSpaces,
  OpenMessage,
  OrderTypes,
  OverrideFieldType,
  PrecisionType,
  StopType,
  StrategyStyleType,
  Timeframe,
  TimeRange,
} from "@shared/types";
import {
  ForwardedRef,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import {
  formatTimeoutDisplay,
  getBotAmountData,
  getBotDefaultOrderDisplay,
  getBotLongShortDisplay,
  getBotMarginDisplay,
  getMyBotsStatisticsValues,
  getBotTrailingOrderDisplay,
  getBotTrailingStopDisplay,
  getCancelOnProfitBeforeEntryDisplay,
  getBotDeleteFailedMessage,
  getBotDeleteWarningMessage,
  getBotDeletedMessage,
  getFirstEntryGraceDisplay,
  getOptionsItemValue,
  getStrategy,
  getTakeProfitGraceDisplay,
  isGridPnlBotType,
} from "@shared/features/bots/utils";
import {
  selectFilteredBots,
  selectFormattedBotGraphData,
  selectFormattedBotsGraphsData,
  selectFormattedPublicBotGraphData,
} from "@shared/slices/bots.slice";
import { sharedLib } from "@shared/services/shared.service";
import { BotsTab, PublicBotSummaryTabs } from "@shared/features/bots/types";
import { get } from "lodash";
import { FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/query";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import {
  getAdvancedKeys,
  getBotStopType,
  getRawStrategyPriceRange,
  getStopLimitPriceReduction,
  isBuiltInStrategy,
  isCustomStrategy,
  isDcaStrategy,
} from "@shared/features/bots/BotForm/common/utils";
import {
  DISPLAY_CURRENCIES,
  STOP_TYPE_TO_DISPLAY,
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_START,
} from "@shared/common/constants";
import {
  CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE,
  DEFAULT_GRID_BOT_STRATEGY,
  STRATEGY_BASELINE_TO_DISPLAY,
} from "@shared/features/bots/BotForm/common/constants";
import {
  OverrideType,
  StrategyBaseline,
} from "@shared/features/bots/BotForm/common/types";
import { getLocaleStringWithPrecision, toFixed } from "@shared/utils/numbers";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { useTheme } from "styled-components";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  ACTIVE_TRADE_ROWS_BREAKPOINT,
  USING_CHANNEL_SETTING,
  VERTICAL_ACTIVE_TRADE_BREAKPOINT,
} from "@shared/features/bots/constants";
import { checkSymbolSearch } from "@shared/utils/misc";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import { isSymbol } from "@shared/types/utils";
import {
  useSubUserPermissions,
  useSymbolsMetadata,
} from "@shared/utils/hooks/misc";
import { isValidUuid } from "@shared/common/utils";
import { getFormattedChartXValue } from "@shared/utils/charts";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import { translateOptionName } from "@shared/common/i18n/optionNames";

export const useCanManageBotType = (botType: BotType) => {
  const { isSubUser, isReadWrite } = useSubUserPermissions();
  return !isSubUser || (!!isReadWrite && botType === BotType.SIGNAL);
};

export const useGetBotsGraphsData = (
  getBotsGraphsDataArgs: GetBotsGraphsDataRequest
) => {
  const getBotsGraphsDataStatus =
    sharedLib.sharedApi.bots.useGetBotsGraphsDataQuery(getBotsGraphsDataArgs);

  const memoizedSelector = useMemo(() => {
    return selectFormattedBotsGraphsData(getBotsGraphsDataArgs);
  }, [getBotsGraphsDataArgs]);

  const graphsData: FormattedBotsGraphsData = useSelector(memoizedSelector);

  return {
    status: getBotsGraphsDataStatus,
    data: graphsData,
    refetch: () => {
      try {
        getBotsGraphsDataStatus.refetch();
      } catch {}
    },
  };
};

export const useSharedBotSummary = (
  botData?: BaseBotData,
  showAccount?: boolean,
  isChannelConfiguration?: boolean,
  backtesting?: BacktestingRunType,
  isAdminConfiguration?: boolean,
  propsShowAmount?: boolean
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const [showOperationHoursCalendar, setShowOperationHoursCalendar] =
    useState(false);
  const hideMissingFields = !!isChannelConfiguration;
  const { getAccountActiveSymbols } = useSymbolsMetadata();
  const { precision: pricePrecision, getPrecision } = usePrecisions(
    botData
      ? {
          precisionType: PrecisionType.PRICE,
          symbol: botData.symbols?.[0],
          exchange: botData.exchange,
        }
      : undefined
  );
  const { getSecondsTimeoutDisplay } = useGetSecondsTimeoutDisplay();
  if (!botData) return {};
  const isTvBot = [BotType.TRADING_VIEW, BotType.TRADING_VIEW_SIGNALS].includes(
    botData.bot_type
  );
  const isSignalsBot = botData.bot_type === BotType.SIGNAL;
  const isGridBot = botData.bot_type === BotType.GRID;
  const isHedgingBot = botData.bot_type === BotType.HEDGING;
  const isTradingViewSignalsBot =
    botData.bot_type === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot =
    botData.bot_type === BotType.TRADING_VIEW || isTradingViewSignalsBot;
  const marginDisplay = getBotMarginDisplay(botData, t);
  const longShortDisplay = getBotLongShortDisplay(botData, "");
  const trailingEntryDisplay = getBotTrailingOrderDisplay(
    botData,
    OrderTypes.ENTRY,
    isChannelConfiguration,
    isAdminConfiguration || isTradingViewSignalsBot,
    t
  );
  const firstEntryGraceDisplay = getFirstEntryGraceDisplay(
    botData,
    hideMissingFields,
    t
  );
  const takeProfitGraceDisplay = getTakeProfitGraceDisplay(
    botData,
    hideMissingFields
  );
  const trailingTakeProfitDisplay = getBotTrailingOrderDisplay(
    botData,
    OrderTypes.TAKE_PROFIT,
    isChannelConfiguration,
    isAdminConfiguration || isTradingViewSignalsBot,
    t
  );
  const defaultTakeProfitDisplay = getBotDefaultOrderDisplay(
    botData,
    OrderTypes.TAKE_PROFIT,
    hideMissingFields,
    t
  );
  const defaultStopDisplay = getBotDefaultOrderDisplay(
    botData,
    OrderTypes.STOP,
    hideMissingFields,
    t
  );
  const stopTimeout = getOptionsItemValue(botData, "Stop Timeout");
  const trailingStopDisplay = getBotTrailingStopDisplay(
    botData,
    isChannelConfiguration,
    isAdminConfiguration || isTradingViewSignalsBot,
    t
  );
  const stopType: StopType | undefined = getBotStopType(
    botData,
    hideMissingFields
  );
  const stopTypeDisplay = stopType
    ? translateOptionName(STOP_TYPE_TO_DISPLAY[stopType])
    : undefined;
  let stopLimitPriceReductionDisplay =
    botData.stop_type === StopType.LIMIT
      ? getStopLimitPriceReduction(botData)
      : undefined;
  if (stopLimitPriceReductionDisplay) {
    stopLimitPriceReductionDisplay = `${stopLimitPriceReductionDisplay}%`;
  }

  const skipWithoutStopLossDisplay =
    isChannelConfiguration || !isSignalsBot
      ? undefined
      : botData.skip_without_stoploss
      ? t("labels.on")
      : t("labels.off");

  const isAdvanced =
    !!trailingEntryDisplay ||
    !!trailingTakeProfitDisplay ||
    !!defaultStopDisplay ||
    !!stopTimeout;

  const cancelOnProfitBeforeEntryDisplay = getCancelOnProfitBeforeEntryDisplay(
    botData,
    hideMissingFields,
    isAdminConfiguration,
    isChannelConfiguration,
    t
  );
  const cancelOnProfitBeforeEntryDisplayData = {
    shouldDisplay: !!cancelOnProfitBeforeEntryDisplay,
    title: t("labels.close_trade_on_tp_before_entry"),
    helperData: {
      analyticsFieldName: t(CANCEL_ON_PROFIT_BEFORE_ENTRY_TITLE),
      description: t("descriptions.close_trade_on_tp_before_entry"),
      analyticsPageName: AnalyticsPageName.BOT_SUMMARY,
      articleId: "5814872",
    },
    value: cancelOnProfitBeforeEntryDisplay,
  };

  let defaultTakeProfitTitle = t("labels.default_take_profit");
  if (botData.bot_type === BotType.SIGNAL) {
    defaultTakeProfitTitle = t("labels.first_take_profit_distance");
  }

  const isStringDefaultTakeProfit =
    typeof defaultTakeProfitDisplay === "string";
  const dynamicTakeProfitData = {
    shouldDisplay:
      isSignalsBot &&
      !isStringDefaultTakeProfit &&
      defaultTakeProfitDisplay?.baseline,
    title: t("labels.moving_take_profits"),
    value: isStringDefaultTakeProfit
      ? undefined
      : defaultTakeProfitDisplay?.baseline,
  };

  const defaultTakeProfitData = {
    shouldDisplay:
      !!defaultTakeProfitDisplay &&
      (isStringDefaultTakeProfit || defaultTakeProfitDisplay?.distance),
    title: defaultTakeProfitTitle,
    value: defaultTakeProfitDisplay,
    showBaseline: !isSignalsBot,
  };

  const getSymbols = () => {
    if (botData.symbols || isTvBot) return botData.symbols;
    const accountSymbols = getAccountActiveSymbols(botData.client_id);
    if (botData.blacklist_symbols?.length) {
      const blacklistSet = new Set(botData.blacklist_symbols);
      return accountSymbols?.filter((symbol) => !blacklistSet.has(symbol));
    }
    return accountSymbols;
  };

  const symbols = getSymbols();
  const numSymbols = symbols?.length || 0;
  const symbolsData = {
    shouldDisplay: "symbols" in botData,
    isMultiSymbol: !!symbols && symbols.length > 1,
    isFromTvAlert: isTvBot && !botData.symbols,
    symbol: symbols?.[0],
    symbols,
    numSymbols: numSymbols,
    title: numSymbols === 1 ? t("labels.symbol") : t("labels.symbols"),
    includeNewlyListedDisplay:
      !isSignalsBot || isChannelConfiguration || isAdminConfiguration
        ? undefined
        : `* ${t("labels.include_newly_listed_symbols")}: ${
            botData.symbols ? t("labels.off") : t("labels.on")
          }`,
  };

  const account =
    "exchange" in botData && "client_name" in botData
      ? { exchange: botData.exchange, accountName: botData.client_name }
      : undefined;
  const accountData = {
    title: t("labels.account"),
    shouldDisplay: showAccount && !!account,
    account,
  };

  const subGroupData = {
    shouldDisplay:
      isSignalsBot ||
      (isTradingViewSignalsBot &&
        "sub_group_id" in botData &&
        !isChannelConfiguration),
    title: t("labels.group"),
  };

  const exchangesData = {
    shouldDisplay: isTradingViewSignalsBot,
    title: "Exchanges",
  };

  const stopTimeoutData = {
    title: t("labels.stop_timeout"),
    shouldDisplay:
      !isTradingViewSignalsBot &&
      (!hideMissingFields || "Stop Timeout" in (botData.options || {})),
    value: stopTimeout
      ? getSecondsTimeoutDisplay(stopTimeout * 60)
      : t("labels.off"),
  };

  const alternativeUsdPairsData = {
    title: t("labels.alternative_usd_pairs"),
    shouldDisplay: isSignalsBot && !isChannelConfiguration,
    value: botData.alternative_usd_pairs
      ? botData.alternative_usd_pairs?.join(", ")
      : t("labels.off"),
  };

  const operationHoursData = {
    title: t("labels.operation_hours"),
    shouldDisplay: isSignalsBot,
    value: botData.blacklisted_operation_hours?.length
      ? t("labels.on")
      : t("labels.off"),
    showButton: !!botData.blacklisted_operation_hours?.length,
    onShowMoreClick: () => setShowOperationHoursCalendar(true),
    drawerOpen: showOperationHoursCalendar,
    closeDrawer: () => setShowOperationHoursCalendar(false),
    data: botData.blacklisted_operation_hours,
  };

  const { amountType } = getBotAmountData(botData);
  const showAmount =
    (!!amountType || isTradingViewSignalsBot) &&
    (propsShowAmount || isChannelConfiguration);
  const showGeneralSection =
    !hideMissingFields ||
    symbolsData.shouldDisplay ||
    accountData.shouldDisplay ||
    subGroupData.shouldDisplay ||
    showAmount;

  const showAdvancedSection =
    (!hideMissingFields && !isHedgingBot) ||
    getAdvancedKeys(botData.bot_type).some(
      (advancedKey) => !!botData[advancedKey]
    ) ||
    cancelOnProfitBeforeEntryDisplayData.shouldDisplay ||
    !!stopTypeDisplay ||
    !!stopLimitPriceReductionDisplay ||
    alternativeUsdPairsData.shouldDisplay;

  const showTrailingStop =
    [
      BotType.TRADING_VIEW,
      BotType.TRADING_VIEW_SIGNALS,
      BotType.SIGNAL,
    ].includes(botData.bot_type) && trailingStopDisplay;
  const showNumContinuousStops = [BotType.ASAP, BotType.TRADING_VIEW].includes(
    botData.bot_type
  );

  const showTakeProfitStrategy =
    [
      BotType.TRADING_VIEW,
      BotType.TRADING_VIEW_SIGNALS,
      BotType.SIGNAL,
    ].includes(botData.bot_type) &&
    (!isChannelConfiguration || !!botData.selling_strategy);

  const showEntries =
    !isHedgingBot &&
    (!isChannelConfiguration ||
      botData.buying_strategy ||
      trailingEntryDisplay ||
      firstEntryGraceDisplay);
  const showTakeProfits =
    !isGridBot &&
    (trailingTakeProfitDisplay ||
      dynamicTakeProfitData?.shouldDisplay ||
      showTakeProfitStrategy);
  const showStop =
    !!defaultStopDisplay ||
    !!skipWithoutStopLossDisplay ||
    showNumContinuousStops ||
    stopTimeoutData?.shouldDisplay ||
    showTrailingStop;

  const sectionsDisplayData = {
    entries: showEntries,
    takeProfits: showTakeProfits,
    stop: showStop,
    isEmptyConfiguration:
      !showGeneralSection && !showEntries && !showTakeProfits && !showStop,
  };

  const initialFundsData = {
    mainCoinsData: Object.entries(
      backtesting?.initial_portfolio_size.main_coin || {}
    ).map(([coin, funds]) => [
      coin,
      toFixed(
        funds,
        getPrecision(PrecisionType.AMOUNT, coin, account?.exchange)
      ),
    ]) as [string, number][],
    analyticsData: {
      analyticsPageName: AnalyticsPageName.BACKTESTING,
      analyticsFieldName: "Initial Funds",
      description:
        "The initial investment amount used for the backtest, broken down by coins.",
    },
  };

  const trailingTakeProfitLastTargetOnly =
    botData.trailing_take_profit_last_target_only;

  return {
    symbolsData,
    accountData,
    subGroupData,
    exchangesData,
    showGeneralSection,
    marginDisplay,
    longShortDisplay,
    trailingEntryDisplay,
    trailingTakeProfitDisplay,
    trailingTakeProfitLastTargetOnly,
    trailingStopDisplay,
    dynamicTakeProfitData,
    defaultTakeProfitData,
    defaultStopDisplay,
    stopTimeoutData,
    showAdvancedSection,
    cancelOnProfitBeforeEntryDisplayData,
    isAdvanced,
    pricePrecision,
    showTakeProfitStrategy,
    showTrailingStop,
    firstEntryGraceDisplay,
    stopTypeDisplay,
    stopLimitPriceReductionDisplay,
    showNumContinuousStops,
    isSignalsBot,
    sectionsDisplayData,
    skipWithoutStopLossDisplay,
    initialFundsData,
    isTradingViewBot,
    showAmount,
    alternativeUsdPairsData,
    takeProfitGraceDisplay,
    operationHoursData,
  };
};

export const useSharedBotStrategySummary = (
  botData: BaseBotData,
  orderType: OrderTypes,
  isAdvanced?: boolean,
  pricePrecision?: number,
  isChannelConfiguration?: boolean,
  isAdminConfiguration?: boolean
) => {
  const isGridBot = botData.bot_type === BotType.GRID;
  const isSignalsBot = botData.bot_type === BotType.SIGNAL;
  const isHedgingBot = botData.bot_type === BotType.HEDGING;
  const isTradingViewSignalsBot =
    botData.bot_type === BotType.TRADING_VIEW_SIGNALS;
  const isEntry = orderType === OrderTypes.ENTRY;
  const strategyData = getStrategy(
    botData,
    orderType,
    botData.bot_type,
    isChannelConfiguration
  );

  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const title =
    orderType === OrderTypes.ENTRY
      ? t(isHedgingBot ? "labels.hedging_strategy" : "labels.entry_strategy")
      : t("labels.take_profit_strategy");
  if (isSymbol(strategyData))
    return {
      title,
      displayString: t(USING_CHANNEL_SETTING),
    };
  if (!strategyData) {
    if (isChannelConfiguration || isHedgingBot) {
      return;
    } else if (isTradingViewSignalsBot) {
      return {
        title,
        displayString: translateOptionName(OverrideType.USER_CONFIGURATION),
      };
    } else if (isAdminConfiguration) {
      return {
        signalsBotStrategyData: [
          {
            title: isEntry
              ? t("labels.entries_ratios")
              : t("labels.take_profits_ratios"),
            value: translateOptionName(OverrideType.USER_CONFIGURATION),
            targetsDisplay: undefined,
            overrideType: undefined,
          },
        ],
        title,
      };
    }
    return {
      title,
      displayString: isChannelConfiguration
        ? t("labels.evenly_divided")
        : t("labels.off"),
    };
  }

  const {
    price_difference: priceDifference,
    price_scale: priceScale,
    number_of_active_orders: numberOfActiveOrders,
    baseline: rawBaseline,
    strategy_style: strategyStyle,
  } = strategyData;
  let targetsDisplay: string[] | undefined,
    amount_scale: number | undefined,
    number_of_orders: number | undefined,
    first_order_percentage: number | undefined,
    baseline: StrategyBaseline | undefined;

  const getTargetsData = (targets?: CustomStrategyData["targets"]) => {
    if (isSymbol(targets) || !targets) return;
    const lastNonZeroIndex = targets
      ?.slice()
      .reverse()
      .findIndex((percent) => percent !== 0);
    const filteredTargets = targets?.slice(
      0,
      targets.length - (lastNonZeroIndex || 0)
    );
    const targetsLength = filteredTargets?.length || 0;
    return filteredTargets?.map((percent, index) => {
      return `${index + 1} (${percent}%)${
        index !== targetsLength - 1 ? "," : ""
      }`;
    });
  };

  if (isCustomStrategy(strategyData)) {
    targetsDisplay = getTargetsData(strategyData.targets);
  }
  if (
    strategyStyle === StrategyStyleType.DCA ||
    strategyStyle === StrategyStyleType.BUILT_IN
  ) {
    number_of_orders = isSymbol(strategyData.number_of_orders)
      ? undefined
      : strategyData.number_of_orders;
    amount_scale = isSymbol(strategyData.amount_scale)
      ? undefined
      : strategyData.amount_scale;
    baseline = isSymbol(rawBaseline) ? undefined : rawBaseline;
  }
  if (isDcaStrategy(strategyData)) {
    first_order_percentage = isSymbol(strategyData.first_order_percentage)
      ? undefined
      : strategyData.first_order_percentage;
  }
  const finalIsAdvanced =
    isAdvanced ||
    amount_scale !== DEFAULT_GRID_BOT_STRATEGY.amount_scale ||
    priceScale !== DEFAULT_GRID_BOT_STRATEGY.price_scale ||
    numberOfActiveOrders !==
      DEFAULT_GRID_BOT_STRATEGY.number_of_active_orders ||
    baseline !== DEFAULT_GRID_BOT_STRATEGY.baseline;

  const isChannelRatios =
    (isCustomStrategy(strategyData) &&
      (!strategyData.targets || isSymbol(strategyData.targets))) ||
    (isBuiltInStrategy(strategyData) &&
      (!strategyData.builtin_strategy_type ||
        isSymbol(strategyData.builtin_strategy_type))) ||
    (isDcaStrategy(strategyData) &&
      (!strategyData.amount_scale || isSymbol(strategyData.amount_scale)));

  const getGridBotData = () => {
    if (!isGridBot && !isHedgingBot) return;
    const priceRange = getRawStrategyPriceRange(
      strategyData,
      botData.max_grid_price,
      !!botData.reverse,
      pricePrecision
    );
    let gridType = "Arithmetic";
    if (priceScale !== 1) gridType = "Custom";
    else if (baseline === StrategyBaseline.PREVIOUS_ORDER)
      gridType = "Geometric";
    return [
      t("summary_values.price_range", {
        min: priceRange?.min,
        max: priceRange?.max,
      }),
      t("summary_values.number_of_grids", { value: number_of_orders }),
      t("summary_values.grid_type", { value: translateOptionName(gridType) }),
    ];
  };

  const getStrategyData = () => {
    if ((isGridBot && !finalIsAdvanced) || isSignalsBot) return;
    const strategyData: string[] = [];
    if (isGridBot && priceScale !== 1 && baseline)
      strategyData.push(
        t("summary_values.baseline", {
          value: translateOptionName(STRATEGY_BASELINE_TO_DISPLAY[baseline]),
        })
      );
    if (first_order_percentage)
      strategyData.push(
        t("summary_values.first_order_percent", {
          value: first_order_percentage,
        })
      );
    if (amount_scale)
      strategyData.push(
        t("summary_values.amount_scale", { value: amount_scale })
      );
    if (number_of_orders && !isGridBot && !isHedgingBot)
      strategyData.push(
        t("summary_values.number_of_orders", { value: number_of_orders })
      );
    if (numberOfActiveOrders && !isSymbol(numberOfActiveOrders))
      strategyData.push(
        t("summary_values.number_of_active_orders", {
          value: numberOfActiveOrders,
        })
      );
    if (priceDifference && !isSymbol(priceDifference))
      strategyData.push(
        t("summary_values.price_difference", {
          value: toFixed(priceDifference, 2),
        })
      );
    if (priceScale && !isSymbol(priceScale))
      strategyData.push(t("summary_values.price_scale", { value: priceScale }));
    return strategyData;
  };

  const getSignalsBotRatiosData = () => {
    const channelRatiosValue = isChannelConfiguration
      ? t("labels.evenly_divided")
      : t(USING_CHANNEL_SETTING);
    let preview: string | string[] = channelRatiosValue;
    if (
      isBuiltInStrategy(strategyData) &&
      strategyData.builtin_strategy_type &&
      !isSymbol(strategyData.builtin_strategy_type)
    ) {
      preview = strategyData.name || "";
    }
    if (
      isDcaStrategy(strategyData) &&
      amount_scale &&
      !isSymbol(amount_scale)
    ) {
      preview = [];
      if (first_order_percentage)
        preview.push(
          t("summary_values.first_order_percent", {
            value: first_order_percentage,
          })
        );
      if (amount_scale)
        preview.push(t("summary_values.amount_scale", { value: amount_scale }));
    }
    return {
      title: isEntry
        ? t("labels.entries_ratios")
        : t("labels.take_profits_ratios"),
      value: isChannelRatios ? channelRatiosValue : preview,
      targetsDisplay,
      overrideType: isEntry
        ? OverrideFieldType.ENTRY_STRATEGY
        : OverrideFieldType.TAKE_PROFIT_STRATEGY,
    };
  };

  const getSignalsBotStrategyData = () => {
    if (!isSignalsBot && !isAdminConfiguration) return;
    const strategyDisplayData: {
      title: string;
      value: string | string[];
      targetsDisplay?: string[];
      overrideType?: OverrideFieldType;
    }[] = [];
    strategyDisplayData.push(getSignalsBotRatiosData());
    if (!isChannelConfiguration && !isAdminConfiguration) {
      const isChannelPrices = isSymbol(priceDifference) || isSymbol(priceScale);
      const priceStrategyPreview = isChannelPrices
        ? t(USING_CHANNEL_SETTING)
        : [
            t("summary_values.price_difference", {
              value: toFixed(priceDifference, 2),
            }),
            t("summary_values.price_scale", { value: priceScale }),
          ];
      strategyDisplayData.push({
        title: isEntry
          ? t("strategy.entries_prices")
          : t("strategy.take_profits_prices"),
        value: priceStrategyPreview,
      });
      const isMaxNumOrders = isChannelPrices || isChannelRatios;
      strategyDisplayData.push({
        title: isMaxNumOrders
          ? t("strategy.max_number_of_orders")
          : t("strategy.num_orders"),
        value:
          isSymbol(number_of_orders) || !number_of_orders
            ? t(USING_CHANNEL_SETTING)
            : number_of_orders.toString(),
      });
    }
    return strategyDisplayData;
  };

  return {
    gridsData: getGridBotData(),
    targetsDisplay,
    strategyData: getStrategyData(),
    signalsBotStrategyData: getSignalsBotStrategyData(),
    title,
  };
};

export const useFilterBotsBySearch = <T>(
  bots: T[] | undefined,
  searchValue: string | undefined,
  tab: BotsTab
) => {
  const SEARCH_PATHS = ["name", "symbols[0]"];
  if (tab === BotsTab.MY_BOTS) {
    SEARCH_PATHS.push("client_name");
  }

  return useMemo(() => {
    if (searchValue && bots) {
      return bots.filter((botData) =>
        SEARCH_PATHS.some((searchPath) =>
          checkSymbolSearch(get(botData, searchPath, ""), searchValue)
        )
      );
    }
    return bots;
  }, [bots, searchValue]);
};

export const usePublicBotPage = (
  botUUID?: string,
  isDemoCopyPublic?: boolean
) => {
  const isValidBotUuid = isValidUuid(botUUID);
  const [pageTab, setPageTab] = useState<PublicBotSummaryTabs>(
    PublicBotSummaryTabs.SUMMARY
  );
  const [chartTab, setChartTab] = useState<BotGraphType>(
    BotGraphType.ACCUMULATING_PROFIT
  );

  const getBotDataStatus = sharedLib.sharedApi.bots.useGetPublicBotDataQuery(
    isValidBotUuid
      ? { dca_bot_uuid: botUUID, is_demo_query: !!isDemoCopyPublic }
      : skipToken
  );
  sharedLib.sharedApi.configuration.useGetClientsListQuery();

  const publicBotErrors = {
    isInvalidLink: !isValidBotUuid,
    isPrivate: (getBotDataStatus.error as FetchBaseQueryError)?.status === 403,
    generalError: !!getBotDataStatus.error,
  };
  const publicBotData = getBotDataStatus?.data?.public_data;

  return {
    pageTab,
    setPageTab,
    chartTab,
    setChartTab,
    getBotDataStatus,
    publicBotErrors,
    publicBotData,
  };
};

export interface useBulkDeleteBotsModal {
  botsIDs?: number[];
  displayResultMessage?: DisplayResultMessage;
}

export const useBulkDeleteBotsModal = ({
  botsIDs,
  displayResultMessage,
}: useBulkDeleteBotsModal) => {
  const { botType } = useContext(MyBotsPageContext)!;
  const [bulkDeleteBotsModalOpen, setBulkDeleteBotsModalOpen] = useState(false);
  const [deleteBots, { isLoading }] =
    sharedLib.sharedApi.bots.useDeleteBotsMutation();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const handleDeleteBots = () => {
    if (botsIDs) {
      return displayResultMessage?.(
        deleteBots({ bot_ids: botsIDs }).unwrap(),
        getBotDeletedMessage(botType, botsIDs.length > 1, t),
        getBotDeleteFailedMessage(botType, botsIDs.length > 1, t)
      )
        .then(() => setBulkDeleteBotsModalOpen(false))
        .catch(() => {});
    }
  };

  const content = getBotDeleteWarningMessage(
    !!botsIDs && botsIDs.length > 1,
    t
  );

  const openModal = () => setBulkDeleteBotsModalOpen(true);
  const closeModal = () => setBulkDeleteBotsModalOpen(false);

  return {
    onOk: handleDeleteBots,
    content,
    isLoading,
    openModal,
    closeModal,
    isOpen: bulkDeleteBotsModalOpen,
  };
};

export const useMyBotsStatistics = (botType: BotType) => {
  const { getBotsDataArgs } = useContext(MyBotsPageContext)!;
  const theme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();

  const selector = useMemo(() => {
    return getBotsDataArgs === skipToken
      ? () => undefined
      : selectFilteredBots(getBotsDataArgs);
  }, [getBotsDataArgs]);

  const filteredBotsData = useSelector(selector);

  const { isFetching } =
    sharedLib.sharedApi.bots.useGetBotsDataQuery(getBotsDataArgs);
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const profitPrecision = getDisplayCurrencyAmountPrecision();
  const useGridPnl = isGridPnlBotType(botType);
  const isHedgingBots = botType === BotType.HEDGING;

  const [numberOfActions, profit] = useMemo(() => {
    const [totalActions, totalProfit] = getMyBotsStatisticsValues(
      filteredBotsData,
      botType,
      displayCurrency
    );
    return [totalActions, toFixed(totalProfit, profitPrecision)];
  }, [filteredBotsData, botType, displayCurrency, profitPrecision]);

  let profitTitleType;
  let profitTitleColor;
  if (profit > 0) {
    profitTitleType = "success";
    profitTitleColor = theme.colors.green;
  } else if (profit < 0) {
    profitTitleType = "danger";
    profitTitleColor = theme.colors.red;
  }

  return {
    isFetching,
    numClosedData: {
      title: isHedgingBots
        ? t("labels.rebalances")
        : useGridPnl
        ? t("labels.take_profits_filled")
        : t("labels.closed_trades"),
      value: numberOfActions,
    },
    profitData: {
      title: useGridPnl ? t("labels.realized_pnl") : t("labels.profit"),
      value: getLocaleStringWithPrecision(profit, profitPrecision),
      type: profitTitleType,
      color: profitTitleColor,
    },
  };
};

export const useActiveTradePreview = (
  botData: BotData | BasicBotData,
  width: number
) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { active_trade: activeTrade } = botData;
  const { last_entry } = activeTrade || {};

  const isGridBot = botData.bot_type === BotType.GRID;
  const useVerticalStatistics =
    width < VERTICAL_ACTIVE_TRADE_BREAKPOINT &&
    width >= ACTIVE_TRADE_ROWS_BREAKPOINT;
  const useActiveTradeRows = width < ACTIVE_TRADE_ROWS_BREAKPOINT;

  const lastOrderData = {
    shouldDisplay: !!last_entry,
    title: isGridBot
      ? t("labels.last_grid_filled")
      : t("labels.last_entry_filled"),
    texts: [
      `#${last_entry?.target_number}/${last_entry?.number_of_targets}`,
      `${last_entry?.time_passed} ago`,
    ],
  };

  const dividerText = isGridBot
    ? t("labels.active_grid")
    : t("labels.active_trade");
  const noActiveTradeText = isGridBot
    ? t("labels.no_active_grid")
    : t("labels.no_active_trade");

  return {
    useVerticalStatistics,
    useActiveTradeRows,
    lastOrderData,
    dividerText,
    noActiveTradeText,
    activeTrade,
  };
};

export const useBotStatusTag = (bot: BotData | BasicBotData) => {
  const theme = useTheme();
  const { getInvalidAccountData } = useInvalidAccount();

  const invalidAccountData = getInvalidAccountData(bot.client_id);
  const isBotActive = bot.status === BotStatus.ACTIVE;
  const isBotArchived = bot.status === BotStatus.ARCHIVED;

  let color = theme.colors.green;
  if (isBotArchived) color = theme.colors.secondaryGrey;
  else if (invalidAccountData.isInvalidOrInactive && isBotActive)
    color = theme.colors.gold;
  else if (invalidAccountData.isInvalidOrInactive || !isBotActive)
    color = theme.colors.red;

  return {
    color,
    invalidAccountData,
    isBotActive,
    isBotArchived,
  };
};

interface useBotChartProps {
  botId?: number;
  publicBotUUID?: string;
  chartType: BotGraphType;
  botData?: BotData;
  defaultTimeRange?: TimeRange;
  ref?: ForwardedRef<any>;
  isBacktesting?: boolean;
  liveChartData?: {
    data?: FormattedChartDataPoint[];
    timeframe?: Timeframe;
    currentJobTimestamp?: number;
  };
  isDemoCopyPublic?: boolean;
}

export const useBotChart = ({
  botId,
  publicBotUUID,
  chartType,
  botData,
  defaultTimeRange = TIME_RANGE_1_MONTHS,
  ref,
  isBacktesting,
  liveChartData,
  isDemoCopyPublic,
}: useBotChartProps) => {
  const [timeRange, setTimeRange] = useState<TimeRange>(defaultTimeRange);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const {
    data: liveGraphData,
    timeframe,
    currentJobTimestamp,
  } = liveChartData || {};

  const args = useMemo(() => {
    const baseRequest = {
      graph_type: chartType,
      time_range: timeRange,
    };
    if (publicBotUUID) {
      return {
        ...baseRequest,
        dca_bot_uuid: publicBotUUID,
        is_demo_query: !!isDemoCopyPublic,
      };
    } else {
      return {
        ...baseRequest,
        dca_bot_id: botId,
        is_backtesting: isBacktesting,
      };
    }
  }, [botId, chartType, timeRange, isBacktesting, isDemoCopyPublic]);

  const startTimeArgs = useMemo(() => {
    return { ...args, time_range: TIME_RANGE_START };
  }, [args]);

  const getBotGraphDataStatus =
    sharedLib.sharedApi.bots.useGetBotGraphDataQuery(
      botId && !liveGraphData ? (args as GetBotGraphDataRequest) : skipToken
    );
  const getPublicBotGraphDataStatus =
    sharedLib.sharedApi.bots.useGetPublicBotGraphDataQuery(
      publicBotUUID ? (args as GetPublicBotGraphDataRequest) : skipToken
    );
  const getPublicBotStartGraphDataStatus =
    sharedLib.sharedApi.bots.useGetPublicBotGraphDataQuery(
      publicBotUUID && chartType === BotGraphType.ACCUMULATING_PROFIT_PERCENTAGE
        ? (startTimeArgs as GetPublicBotGraphDataRequest)
        : skipToken
    );

  const startGraphData =
    getPublicBotStartGraphDataStatus?.data?.data[TIME_RANGE_START];
  const totalChangePercent = startGraphData?.length
    ? startGraphData[startGraphData.length - 1][1]
    : undefined;

  const graphDataSelector = useMemo(() => {
    return botId
      ? selectFormattedBotGraphData(args as GetBotGraphDataRequest, botData)
      : selectFormattedPublicBotGraphData(
          args as GetPublicBotGraphDataRequest,
          botData,
          totalChangePercent as number
        );
  }, [args, botData, botId, publicBotUUID, totalChangePercent]);
  const graphData = useSelector(
    liveGraphData ? () => liveGraphData : graphDataSelector
  );
  const displayCurrency = useSelector(selectDisplayCurrency);

  const isFetching =
    getBotGraphDataStatus.isFetching ||
    getPublicBotGraphDataStatus.isFetching ||
    (liveGraphData && !graphData?.length);
  const isSuccess = botId
    ? getBotGraphDataStatus.isSuccess
    : getPublicBotGraphDataStatus.isSuccess;
  const isPercent = [
    BotGraphType.PROFIT_BY_DAY_PERCENTAGE,
    BotGraphType.ACCUMULATING_PROFIT_PERCENTAGE,
  ].includes(chartType);
  const isLineChart = [
    BotGraphType.ACCUMULATING_PROFIT,
    BotGraphType.ACCUMULATING_PROFIT_PERCENTAGE,
  ].includes(chartType);

  const refetch = () => {
    try {
      getBotGraphDataStatus.refetch();
    } catch {}
    try {
      getPublicBotGraphDataStatus.refetch();
    } catch {}
  };

  useImperativeHandle(ref, () => ({ refetch }));

  const xMinMax = timeframe
    ? {
        min: timeframe?.start,
        max: timeframe?.end,
      }
    : undefined;
  const strXMinMax = xMinMax
    ? {
        min: getFormattedChartXValue(xMinMax.min) as string,
        max: getFormattedChartXValue(xMinMax.max) as string,
      }
    : undefined;

  const filteredGraphData = useMemo(() => {
    if (!currentJobTimestamp) return liveChartData ? [] : graphData;
    const filteredData = graphData?.filter((dataPoint) => {
      const x = dataPoint.rawX || 0;
      return x < currentJobTimestamp;
    });
    if (!isLineChart && filteredData.length === 1) {
      filteredData.push(graphData?.[1]);
    }
    return filteredData;
  }, [graphData, currentJobTimestamp, isLineChart]);

  return {
    graphData: filteredGraphData,
    displayCurrency,
    isFetching,
    isSuccess,
    isPercent,
    chartPrecision: getDisplayCurrencyAmountPrecision(),
    timeRange,
    setTimeRange,
    isLineChart,
    xMinMax,
    strXMinMax,
  };
};

interface usePublicBotToggleProps {
  displayResultMessage: DisplayResultMessage;
  botData: BaseBotData;
  openMessage: OpenMessage;
}

export const usePublicBotToggle = ({
  displayResultMessage,
  botData,
  openMessage,
}: usePublicBotToggleProps) => {
  const [changeBotPublicity, changeBotPublicityStatus] =
    sharedLib.sharedApi.bots.useChangeBotPublicityMutation();

  const onChange = (checked: boolean) => {
    if (botData.is_copied) {
      openMessage("Copied bots cannot be re-publicized", "warning");
    } else {
      displayResultMessage(
        changeBotPublicity({
          dca_bot_id: botData.id,
          is_public: checked,
        }).unwrap(),
        `The bot is now${checked ? "" : " not"} discoverable by other users`
      ).catch(() => {});
    }
  };

  const helperTooltipData = {
    analyticsFieldName: "Discoverable",
    analyticsPageName: AnalyticsPageName.BOT_EXTRA_DETAILS,
    description:
      "When enabled an anonymized version of this bot is visible and can be copied from the ‘Public Bots’ page",
  };

  return {
    isLoading: changeBotPublicityStatus.isLoading,
    onChange,
    helperTooltipData,
  };
};

export const useGetSecondsTimeoutDisplay = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return {
    getSecondsTimeoutDisplay: (numSeconds: number | undefined) =>
      formatTimeoutDisplay(numSeconds, t),
  };
};
